import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import NZTerritoryMap from "@/components/NZTerritoryMap";
import {
  MapPin, Plus, Trash2, Save, AlertCircle, Search, X, RefreshCw,
  ChevronLeft, Users, Sprout, Mail,
} from "lucide-react";

const STATUS_OPTIONS = [
  { v: "open",    label: "Open",        pill: "bg-[var(--bloom-brand)] text-white" },
  { v: "pending", label: "Coming soon", pill: "bg-[#C9A14A] text-white" },
  { v: "looking", label: "Looking",     pill: "bg-white border border-[var(--bloom-ink-soft)] text-[var(--bloom-ink-soft)]" },
];

export default function ConsoleAreaMap() {
  const [areas, setAreas] = useState([]);
  const [nzRegions, setNzRegions] = useState([]);
  const [growers, setGrowers] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [activeKey, setActiveKey] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/area-map");
      setAreas(data.areas || []);
      setNzRegions(data.nz_regions || []);
      setGrowers(data.growers || []);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return areas.filter((a) => {
      if (statusFilter && a.status !== statusFilter) return false;
      if (!needle) return true;
      return (
        (a.region || "").toLowerCase().includes(needle) ||
        (a.city || "").toLowerCase().includes(needle) ||
        (a.grower_name || "").toLowerCase().includes(needle)
      );
    });
  }, [areas, q, statusFilter]);

  const grouped = useMemo(() => ({
    open:    filtered.filter((a) => a.status === "open"),
    pending: filtered.filter((a) => a.status === "pending"),
    looking: filtered.filter((a) => a.status === "looking"),
  }), [filtered]);

  const totalRealWaitlist = useMemo(() => {
    const byRegion = {};
    areas.forEach((a) => { byRegion[a.region] = a.real_waitlist || 0; });
    return Object.values(byRegion).reduce((s, n) => s + n, 0);
  }, [areas]);

  const quickStatus = async (a, status) => {
    setSavingId(a.id); setErr("");
    try {
      const { data } = await api.patch(`/admin/area-map/${a.id}`, { status });
      setAreas((rows) => rows.map((r) => (r.id === a.id ? data.area : r)));
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  const quickTeaser = async (a, n) => {
    setSavingId(a.id); setErr("");
    try {
      const { data } = await api.patch(`/admin/area-map/${a.id}`, { waitlist_teaser: Math.max(0, Math.min(999, Number(n) || 0)) });
      setAreas((rows) => rows.map((r) => (r.id === a.id ? data.area : r)));
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.label} from the map?`)) return;
    try {
      await api.delete(`/admin/area-map/${a.id}`);
      setAreas((rows) => rows.filter((r) => r.id !== a.id));
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  const reseed = async () => {
    if (!window.confirm("Wipe and reseed the map with default areas? Any custom changes will be lost.")) return;
    await api.post("/admin/area-map/reseed");
    load();
  };

  const openArea = (a) => {
    setActiveKey(`${a.region}__${a.city || ""}`);
    setSelectedId(a.id);
  };

  return (
    <div data-testid="console-area-map">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--bloom-ink-muted)] mb-1">Heritage Bloom Club</div>
        <h1 className="font-serif text-5xl md:text-6xl leading-none">Territory Hub</h1>
        <p className="font-serif italic text-lg text-[var(--bloom-ink-soft)] mt-3">
          One place for the whole map: public pin status, assigned growers, capacity, and real waitlist interest per region.
        </p>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="area-totals">
        <Tile label="Open" value={grouped.open.length} accent />
        <Tile label="Coming soon" value={grouped.pending.length} />
        <Tile label="Looking" value={grouped.looking.length} />
        <Tile label="Real waitlist" value={totalRealWaitlist} accent />
        <Tile label="Teaser total" value={areas.reduce((s, a) => s + (a.waitlist_teaser || 0), 0)} />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--bloom-ink-muted)]" />
          <input
            type="text"
            placeholder="Search city, region or grower"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="bloom-input pl-9 min-w-[260px]"
            data-testid="area-search"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bloom-input max-w-[170px]" data-testid="area-status-filter">
          <option value="">Any status</option>
          {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={reseed} className="bloom-btn bloom-btn-ghost text-sm" data-testid="area-reseed">
            <RefreshCw size={13} /> Reseed defaults
          </button>
          <button onClick={() => setShowNew(true)} className="bloom-btn bloom-btn-primary" data-testid="area-new-btn">
            <Plus size={14} /> Add pin
          </button>
        </div>
      </div>

      {err && (
        <div className="mb-5 flex items-center gap-2 text-sm text-[var(--bloom-accent)] font-serif italic" data-testid="area-error">
          <AlertCircle size={14} /> {err}
        </div>
      )}

      {/* Map + List */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 bloom-card p-4 self-start">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] mb-2">
            Click a pin to manage the territory
          </div>
          <NZTerritoryMap
            areas={areas}
            activeKey={activeKey}
            onPinClick={openArea}
            size="default"
            showLegend
          />
        </div>

        <div className="lg:col-span-7 space-y-6" data-testid="area-list">
          {loading ? (
            <Empty msg="Loading map…" />
          ) : filtered.length === 0 ? (
            <Empty msg="No areas match those filters." />
          ) : (
            ["open", "pending", "looking"].map((status) => {
              const list = grouped[status];
              if (list.length === 0) return null;
              const meta = STATUS_OPTIONS.find((s) => s.v === status);
              return (
                <div key={status} data-testid={`area-group-${status}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-[9px] tracking-[0.2em] uppercase px-2 py-1 ${meta.pill}`}>{meta.label}</span>
                    <div className="flex-1 h-px bg-[var(--bloom-border)]" />
                    <span className="text-[10px] tracking-[0.15em] uppercase text-[var(--bloom-ink-muted)]">{list.length}</span>
                  </div>
                  <div className="space-y-2">
                    {list.map((a) => (
                      <AreaRow
                        key={a.id}
                        a={a}
                        saving={savingId === a.id}
                        onStatus={(s) => quickStatus(a, s)}
                        onTeaser={(n) => quickTeaser(a, n)}
                        onOpen={() => openArea(a)}
                        onRemove={() => remove(a)}
                      />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {selectedId && (
        <TerritoryDrawer
          areaId={selectedId}
          nzRegions={nzRegions}
          growers={growers}
          onClose={() => { setSelectedId(null); setActiveKey(null); }}
          onSaved={() => load()}
        />
      )}

      {showNew && (
        <NewPinModal
          nzRegions={nzRegions}
          onClose={() => setShowNew(false)}
          onSaved={() => { load(); setShowNew(false); }}
        />
      )}
    </div>
  );
}

function Tile({ label, value, accent }) {
  return (
    <div className="bloom-card p-4">
      <div className="bloom-label mb-2">{label}</div>
      <div className={`font-serif text-3xl ${accent ? "text-[var(--bloom-brand)]" : ""}`}>{value}</div>
    </div>
  );
}

function Empty({ msg }) {
  return <div className="bloom-card p-14 text-center font-serif italic text-[var(--bloom-ink-muted)]">{msg}</div>;
}

function AreaRow({ a, saving, onStatus, onTeaser, onOpen, onRemove }) {
  const [teaserLocal, setTeaserLocal] = useState(a.waitlist_teaser || 0);
  useEffect(() => { setTeaserLocal(a.waitlist_teaser || 0); }, [a.waitlist_teaser]);

  return (
    <div className="bloom-card p-3 flex flex-wrap items-center gap-3" data-testid={`area-row-${a.id}`}>
      <button onClick={onOpen} className="flex-1 min-w-[150px] text-left" data-testid={`area-open-${a.id}`}>
        <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]">
          <MapPin size={11} /> {a.region}
          {(a.real_waitlist || 0) > 0 && (
            <span className="text-[var(--bloom-brand)] normal-case tracking-normal font-serif italic">· {a.real_waitlist} real signup{a.real_waitlist !== 1 ? "s" : ""}</span>
          )}
        </div>
        <div className="font-serif text-lg leading-tight" data-testid={`area-label-${a.id}`}>{a.label}</div>
        {a.grower_name ? (
          <div className="text-xs font-serif italic text-[var(--bloom-ink-soft)] mt-0.5">
            ✦ {a.grower_name}
            {(a.subscribers_count || 0) > 0 && <> · {a.subscribers_count} subscriber{a.subscribers_count !== 1 ? "s" : ""}</>}
          </div>
        ) : (
          <div className="text-xs font-serif italic text-[var(--bloom-ink-muted)] mt-0.5">No grower assigned</div>
        )}
      </button>

      <select
        value={a.status}
        onChange={(e) => onStatus(e.target.value)}
        disabled={saving}
        className="bloom-input max-w-[150px] text-sm"
        data-testid={`area-status-${a.id}`}
      >
        {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
      </select>

      <div className="flex items-center gap-1">
        <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]">tease</span>
        <input
          type="number"
          min="0"
          max="999"
          value={teaserLocal}
          onChange={(e) => setTeaserLocal(e.target.value)}
          onBlur={() => Number(teaserLocal) !== (a.waitlist_teaser || 0) && onTeaser(teaserLocal)}
          disabled={saving}
          className="bloom-input w-20 text-sm"
          data-testid={`area-teaser-${a.id}`}
        />
      </div>

      <button onClick={onOpen} className="bloom-btn bloom-btn-ghost text-xs" data-testid={`area-edit-${a.id}`}>
        Manage
      </button>
      <button onClick={onRemove} className="p-1.5 text-[var(--bloom-ink-muted)] hover:text-[var(--bloom-accent)]" aria-label="Remove" data-testid={`area-remove-${a.id}`}>
        <Trash2 size={14} />
      </button>
    </div>
  );
}

/* ---------- Territory management drawer ---------- */
function TerritoryDrawer({ areaId, nzRegions, growers, onClose, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const loadDetail = async () => {
    const { data } = await api.get(`/admin/area-map/${areaId}/detail`);
    setDetail(data);
    const a = data.area;
    setForm({
      region: a.region,
      city: a.city || "",
      status: a.status,
      waitlist_teaser: a.waitlist_teaser || 0,
      grower_id: a.grower_id || "",
      backup_grower_ids: a.backup_grower_ids || [],
      capacity_subscribers: a.capacity_subscribers || 0,
      notes: a.notes || "",
    });
  };

  useEffect(() => { loadDetail(); /* eslint-disable-next-line */ }, [areaId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.patch(`/admin/area-map/${areaId}`, {
        region: form.region,
        city: form.city ? form.city.trim() : null,
        status: form.status,
        waitlist_teaser: Math.max(0, Math.min(999, Number(form.waitlist_teaser) || 0)),
        grower_id: form.grower_id || null,
        backup_grower_ids: form.backup_grower_ids,
        capacity_subscribers: Math.max(0, Number(form.capacity_subscribers) || 0),
        notes: form.notes || "",
      });
      await loadDetail();
      onSaved?.();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const addBackup = (gid) => {
    if (!gid || form.backup_grower_ids.includes(gid) || gid === form.grower_id) return;
    set("backup_grower_ids", [...form.backup_grower_ids, gid]);
  };
  const removeBackup = (gid) => set("backup_grower_ids", form.backup_grower_ids.filter((x) => x !== gid));

  const growerName = (gid) => {
    const g = growers.find((x) => x.id === gid);
    return g ? `${g.name}${g.farm_name ? ` · ${g.farm_name}` : ""}` : gid;
  };

  const a = detail?.area;

  return (
    <div className="fixed inset-0 z-50 bg-[var(--bloom-ink)]/60 backdrop-blur-sm" onClick={onClose} data-testid="territory-drawer-overlay">
      <div className="absolute right-0 top-0 bottom-0 w-full md:w-[680px] bg-[var(--bloom-bg)] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-[var(--bloom-border)] bg-[var(--bloom-bg)]">
          <button onClick={onClose} className="bloom-btn bloom-btn-ghost text-xs" data-testid="territory-drawer-close">
            <ChevronLeft size={14} /> Back
          </button>
          <button onClick={save} disabled={busy || !form} className="bloom-btn bloom-btn-primary text-xs" data-testid="territory-drawer-save">
            <Save size={12} /> {busy ? "Saving…" : "Save changes"}
          </button>
        </div>

        {!detail || !form ? (
          <div className="p-10 font-serif italic text-[var(--bloom-ink-muted)]">Loading territory…</div>
        ) : (
          <div className="p-8 md:p-10" data-testid="territory-drawer">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] mb-2">Territory</div>
            <h2 className="font-serif text-4xl mb-1">{a.label}</h2>
            <div className="text-sm text-[var(--bloom-ink-soft)]">{a.region}</div>

            {/* Live stats */}
            <div className="grid grid-cols-3 gap-3 mt-6">
              <Stat label="Real waitlist" value={a.real_waitlist || 0} />
              <Stat label="Subscribers" value={a.subscribers_count || 0} />
              <Stat label="Capacity" value={form.capacity_subscribers || "—"} />
            </div>

            {err && (
              <div className="mt-4 flex items-center gap-2 text-sm text-[var(--bloom-accent)] font-serif italic" data-testid="territory-drawer-error">
                <AlertCircle size={14} /> {err}
              </div>
            )}

            {/* Public map settings */}
            <DrawerSection title="Public map pin">
              <div className="grid grid-cols-2 gap-4">
                <Field label="NZ region">
                  <select value={form.region} onChange={(e) => set("region", e.target.value)} className="bloom-input" data-testid="territory-region">
                    {nzRegions.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </Field>
                <Field label="City (optional)">
                  <input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="e.g. Queenstown" className="bloom-input" data-testid="territory-city" />
                </Field>
                <Field label="Status">
                  <select value={form.status} onChange={(e) => set("status", e.target.value)} className="bloom-input" data-testid="territory-status">
                    {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                  </select>
                </Field>
                <Field label="Waitlist tease number">
                  <input type="number" min="0" max="999" value={form.waitlist_teaser} onChange={(e) => set("waitlist_teaser", e.target.value)} className="bloom-input" data-testid="territory-teaser" />
                </Field>
              </div>
            </DrawerSection>

            {/* Growers */}
            <DrawerSection title="Growers" icon={<Sprout size={16} />}>
              <Field label="Primary grower">
                <select value={form.grower_id} onChange={(e) => set("grower_id", e.target.value)} className="bloom-input" data-testid="territory-primary-grower">
                  <option value="">Unassigned</option>
                  {growers.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}{g.farm_name ? ` · ${g.farm_name}` : ""}{g.region ? ` (${g.region})` : ""}</option>
                  ))}
                </select>
              </Field>
              {a.primary_grower && (
                <div className="mt-2 text-xs font-serif italic text-[var(--bloom-ink-soft)]" data-testid="territory-primary-info">
                  Currently: {a.primary_grower.name} · {a.primary_grower.email}
                  {a.primary_grower.application_status && <> · application {a.primary_grower.application_status.replace(/_/g, " ")}</>}
                  {" "}· {a.primary_grower.subscribers} subscriber{a.primary_grower.subscribers !== 1 ? "s" : ""}
                </div>
              )}

              <div className="mt-4">
                <div className="bloom-label mb-2">Backup growers</div>
                {form.backup_grower_ids.length === 0 && (
                  <div className="text-xs font-serif italic text-[var(--bloom-ink-muted)] mb-2">None yet.</div>
                )}
                <div className="space-y-1.5 mb-2">
                  {form.backup_grower_ids.map((gid) => (
                    <div key={gid} className="flex items-center justify-between gap-2 text-sm border border-[var(--bloom-border)] bg-white/50 px-3 py-1.5" data-testid={`territory-backup-${gid}`}>
                      <span className="font-serif">{growerName(gid)}</span>
                      <button onClick={() => removeBackup(gid)} className="text-[var(--bloom-ink-muted)] hover:text-[var(--bloom-accent)]" data-testid={`territory-backup-remove-${gid}`}>
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                </div>
                <select value="" onChange={(e) => addBackup(e.target.value)} className="bloom-input" data-testid="territory-backup-add">
                  <option value="">+ Add a backup grower…</option>
                  {growers.filter((g) => g.id !== form.grower_id && !form.backup_grower_ids.includes(g.id)).map((g) => (
                    <option key={g.id} value={g.id}>{g.name}{g.farm_name ? ` · ${g.farm_name}` : ""}</option>
                  ))}
                </select>
              </div>

              <Field label="Subscriber capacity (0 = not set)" className="mt-4">
                <input type="number" min="0" value={form.capacity_subscribers} onChange={(e) => set("capacity_subscribers", e.target.value)} className="bloom-input max-w-[160px]" data-testid="territory-capacity" />
              </Field>
            </DrawerSection>

            {/* Real waitlist entries */}
            <DrawerSection title={`Waitlist interest in ${a.region} (${(detail.waitlist_entries || []).length})`} icon={<Users size={16} />}>
              {(detail.waitlist_entries || []).length === 0 ? (
                <div className="text-sm font-serif italic text-[var(--bloom-ink-muted)]">No waitlist signups from this region yet.</div>
              ) : (
                <div className="space-y-2" data-testid="territory-waitlist-entries">
                  {detail.waitlist_entries.map((w) => (
                    <div key={w.id} className="flex items-center justify-between gap-3 text-sm border-l-2 border-[var(--bloom-accent)] pl-3 py-1" data-testid={`territory-waitlist-${w.id}`}>
                      <div>
                        <div className="font-serif">{w.name}</div>
                        <div className="text-xs text-[var(--bloom-ink-muted)]">
                          {w.email}{w.postcode ? ` · ${w.postcode}` : ""}{w.box_size ? ` · ${w.box_size} box` : ""}{w.frequency ? ` · ${w.frequency}` : ""}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-[10px] text-[var(--bloom-ink-muted)]">{w.created_at ? new Date(w.created_at).toLocaleDateString() : ""}</span>
                        <a href={`mailto:${w.email}`} className="text-[var(--bloom-ink-muted)] hover:text-[var(--bloom-ink)]" title="Email"><Mail size={13} /></a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </DrawerSection>

            {/* Notes */}
            <DrawerSection title="Internal notes">
              <textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bloom-input" data-testid="territory-notes" />
            </DrawerSection>
          </div>
        )}
      </div>
    </div>
  );
}

function DrawerSection({ title, icon, children }) {
  return (
    <div className="border-t border-[var(--bloom-border)] mt-6 pt-6">
      <h3 className="font-serif text-2xl mb-4 flex items-center gap-2">{icon}{title}</h3>
      {children}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border-l-2 border-[var(--bloom-accent)] pl-3">
      <div className="bloom-label" style={{ fontSize: "0.65rem" }}>{label}</div>
      <div className="font-serif text-2xl">{value}</div>
    </div>
  );
}

/* ---------- New pin modal ---------- */
function NewPinModal({ nzRegions, onClose, onSaved }) {
  const [form, setForm] = useState({ region: nzRegions[0] || "", city: "", status: "looking", waitlist_teaser: 0 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/admin/area-map", {
        region: form.region,
        city: form.city ? form.city.trim() : null,
        status: form.status,
        waitlist_teaser: Math.max(0, Math.min(999, Number(form.waitlist_teaser) || 0)),
      });
      onSaved?.();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--bloom-ink)]/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="area-editor-modal">
      <div className="bg-[var(--bloom-bg)] w-full max-w-xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--bloom-border)]">
          <h2 className="font-serif text-2xl">Add a pin</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--bloom-surface)]" data-testid="area-editor-close"><X size={16} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <Field label="NZ region *">
              <select value={form.region} onChange={(e) => set("region", e.target.value)} className="bloom-input" data-testid="area-editor-region">
                {nzRegions.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="City (optional)">
              <input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="e.g. Queenstown" className="bloom-input" data-testid="area-editor-city" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Status">
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className="bloom-input" data-testid="area-editor-status">
                {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Waitlist tease number">
              <input type="number" min="0" max="999" value={form.waitlist_teaser} onChange={(e) => set("waitlist_teaser", e.target.value)} className="bloom-input" data-testid="area-editor-teaser" />
            </Field>
          </div>
          {err && (
            <div className="flex items-center gap-2 text-sm text-[var(--bloom-accent)] font-serif italic">
              <AlertCircle size={14} /> {err}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="bloom-btn bloom-btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} className="bloom-btn bloom-btn-primary" data-testid="area-editor-save">
              <Save size={13} /> {busy ? "Saving…" : "Add pin"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <div className="bloom-label mb-2">{label}</div>
      {children}
    </div>
  );
}
